/**
 * Transacciones. Banner de cotejo con IA + tabla de movimientos paginada.
 *
 * El recuento del banner es real: `review_status = 'necesita_revision'` en la base. El botón
 * «Revisar» filtra la tabla a ese subconjunto — es la única acción de esta pantalla que puede
 * hacer algo útil sin escritura, así que se implementa en lugar de dejarla muerta.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TransactionRow } from '@naar/shared';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { DataTable, TablePagination, cellStyles } from '../components/DataTable';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { SkeletonCard, SkeletonPageHeader } from '../components/Skeleton';
import { formatDayMonth, formatSignedEur } from '../lib/format';
import styles from './TransactionsPage.module.css';

/** Plantilla de columnas literal del maquetado. */
const TEMPLATE = '0.8fr 2fr 1.4fr 1.4fr 1fr';

const COLUMNS = [
  { key: 'date', label: 'Fecha' },
  { key: 'desc', label: 'Descripción' },
  { key: 'cat', label: 'Categoría' },
  { key: 'prop', label: 'Propiedad' },
  { key: 'amount', label: 'Importe', align: 'right' as const },
];

export function TransactionsPage() {
  const [page, setPage] = useState(1);
  const [onlyReview, setOnlyReview] = useState(false);

  const params = {
    page,
    limit: 25,
    ...(onlyReview ? { review_status: 'necesita_revision' } : {}),
  };

  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.transactions(params),
    queryFn: () => api.transactions(params),
  });

  const header = (
    <PageHeader
      title="Transacciones"
      subtitle="Importa movimientos y deja que la IA los categorice y los coteje."
      actions={
        <>
          <Button variant="secondary" size="md" phaseTwo>
            <Icon name="download" size={16} strokeWidth={2} />
            Exportar
          </Button>
          <Button variant="primary" size="md" phaseTwo>
            <Icon name="upload" size={16} strokeWidth={2} />
            Importar
          </Button>
        </>
      }
    />
  );

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <SkeletonCard rows={1} height={72} />
        <div style={{ height: 18 }} />
        <SkeletonCard rows={7} height={44} />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  return (
    <section>
      {header}

      <Card className={styles.banner}>
        <div className={styles.bannerText}>
          <div className={styles.bannerTitle}>Cotejo automático</div>
          <div className={styles.bannerBody}>
            La IA vincula cada movimiento con su propiedad y lo coteja con la carpeta de gastos.{' '}
            {data.needsReviewCount === 0
              ? 'No hay movimientos pendientes de revisión.'
              : `${data.needsReviewCount} ${
                  data.needsReviewCount === 1 ? 'movimiento necesita' : 'movimientos necesitan'
                } tu revisión.`}
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={data.needsReviewCount === 0}
          onClick={() => {
            setOnlyReview((value) => !value);
            setPage(1);
          }}
        >
          {onlyReview ? 'Ver todos' : `Revisar (${data.needsReviewCount})`}
        </Button>
      </Card>

      <Card variant="table">
        {onlyReview ? (
          <div className={styles.filters}>
            <button
              type="button"
              className={`${styles.filterChip} ${styles.filterChipActive}`}
              onClick={() => {
                setOnlyReview(false);
                setPage(1);
              }}
            >
              Solo pendientes de revisión ✕
            </button>
          </div>
        ) : null}

        {data.data.length === 0 ? (
          <EmptyState
            inline
            icon="transfer"
            title="No hay movimientos con este filtro"
            body="Prueba a quitar el filtro o importa un extracto bancario."
          />
        ) : (
          <>
            <DataTable<TransactionRow>
              caption="Movimientos bancarios"
              columns={COLUMNS}
              template={TEMPLATE}
              rows={data.data}
              rowKey={(row) => row.id}
              renderRow={(row) => {
                const needsReview = row.reviewStatus === 'necesita_revision';
                return (
                  <>
                    <span className={cellStyles.cellMuted}>{formatDayMonth(row.bookedAt)}</span>
                    <div className={styles.description}>
                      <div className={styles.descriptionMain}>{row.description}</div>
                      {row.aiCategorized ? (
                        <div className={styles.aiMark}>
                          <span
                            className={
                              needsReview
                                ? `${styles.aiDot} ${styles.aiDotReview}`
                                : styles.aiDot
                            }
                          />
                          {needsReview ? 'necesita tu revisión' : 'categorizado por IA'}
                        </div>
                      ) : null}
                    </div>
                    <span className={cellStyles.cellMuted} style={{ color: 'var(--naar-text)' }}>
                      {row.categoryPath ?? 'Sin categoría'}
                    </span>
                    <span className={cellStyles.cellSecondary}>
                      {row.property?.name ?? '—'}
                    </span>
                    <span
                      className={
                        Number(row.amount) > 0 ? styles.amountPositive : styles.amountNegative
                      }
                    >
                      {formatSignedEur(row.amount)}
                    </span>
                  </>
                );
              }}
              /* Móvil: concepto e importe en la primera línea; fecha, categoría e inmueble debajo. */
              renderCard={(row) => {
                const needsReview = row.reviewStatus === 'necesita_revision';
                return (
                  <>
                    <div className={styles.cardTop}>
                      <span className={styles.descriptionMain}>{row.description}</span>
                      <span
                        className={
                          Number(row.amount) > 0 ? styles.amountPositive : styles.amountNegative
                        }
                      >
                        {formatSignedEur(row.amount)}
                      </span>
                    </div>
                    <div className={styles.cardMeta}>
                      {[formatDayMonth(row.bookedAt), row.categoryPath ?? 'Sin categoría', row.property?.name]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {needsReview ? (
                      <div className={styles.aiMark}>
                        <span className={`${styles.aiDot} ${styles.aiDotReview}`} />
                        necesita tu revisión
                      </div>
                    ) : null}
                  </>
                );
              }}
            />
            <TablePagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onChange={setPage}
              label="movimientos"
            />
          </>
        )}
      </Card>
    </section>
  );
}
